import { call } from '@advanza/api'
import { Icon, LoadingDots } from '@advanza/ui'
import clsx from 'clsx'
import { useEffect, useState } from 'react'

const TARGET_LANGUAGES = {
    en: 'English',
    es: 'Spanish',
    de: 'German',
    nl: 'Dutch',
} as const

type TargetLanguage = keyof typeof TARGET_LANGUAGES

interface TranslationResponse {
    status: 'success' | 'error'
    translatedText?: string
    detectedLanguage?: string
    error?: string
}

interface TranslateButtonProps {
    text: string
    sourceLanguage?: string | null
    targetLanguage?: TargetLanguage
    className?: string
    /** When true, show button disabled when text is empty (keeps layout consistent) */
    showWhenEmpty?: boolean
    /** When true (or when children is passed), render children + button on one row, translation on a new line below */
    stacked?: boolean
    children?: React.ReactNode
}

/**
 * TranslateButton Component
 *
 * Provides a button to translate text to a target language using Google Cloud Translate API.
 * Shows loading state during translation and displays translated text below original.
 */
const TranslateButton = ({
    text,
    sourceLanguage = null,
    targetLanguage = 'en',
    className,
    showWhenEmpty = false,
    stacked = false,
    children,
}: TranslateButtonProps) => {
    const [isTranslating, setIsTranslating] = useState(false)
    const [translatedText, setTranslatedText] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [showTranslation, setShowTranslation] = useState(false)

    const targetLanguageName = TARGET_LANGUAGES[targetLanguage]

    // Reset translation state when text changes
    useEffect(() => {
        setTranslatedText(null)
        setShowTranslation(false)
        setError(null)
    }, [text])

    const handleTranslate = async (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (isTranslating || translatedText) {
            // Toggle translation visibility if already translated
            setShowTranslation(!showTranslation)
            return
        }
        if (!text || !text.trim()) {
            return
        }

        // Clear any previous errors when starting a new translation
        setError(null)
        setIsTranslating(true)

        try {
            const response = (await call('office/translation/translate', {
                json: {
                    text,
                    sourceLanguage,
                    targetLanguage,
                },
            })) as TranslationResponse

            if (response.status === 'success' && response.translatedText) {
                setTranslatedText(response.translatedText)
                setShowTranslation(true)
            } else {
                setError(response.error || 'Translation failed')
            }
        } catch (err: unknown) {
            const errorMessage =
                (err as { error?: string })?.error ||
                (err as { message?: string })?.message ||
                'Failed to translate. Please try again.'
            setError(errorMessage)
        } finally {
            setIsTranslating(false)
        }
    }

    const hasText = Boolean(text?.trim())
    const stackedLayout = stacked || children != null

    const getButtonLabel = (): string => {
        if (!hasText) {
            return 'Enter text to translate'
        }
        if (translatedText) {
            return showTranslation ? 'Hide Translation' : 'Show Translation'
        }
        return `Translate to ${targetLanguageName}`
    }

    if (!showWhenEmpty && !hasText) {
        return null
    }

    const buttonEl = (
        <button
            type="button"
            onClick={handleTranslate}
            disabled={isTranslating || !hasText}
            className={clsx(
                'inline-flex flex-shrink-0 items-center justify-center w-7 h-7 rounded border border-grey-400 bg-grey-100 hover:bg-grey-300 disabled:cursor-not-allowed disabled:opacity-40 transition-colors',
                !stackedLayout && 'mt-2',
            )}
            title={getButtonLabel()}
            aria-label={getButtonLabel()}>
            {isTranslating ? (
                <LoadingDots />
            ) : (
                <Icon
                    name={translatedText && showTranslation ? 'visibility_off' : 'translate'}
                    fontSize={16}
                />
            )}
        </button>
    )

    const translationEl =
        translatedText && showTranslation ? (
            <div
                className={
                    stackedLayout
                        ? 'mt-2 w-full rounded border border-grey-400 bg-grey-100 px-2 py-1.5 text-left text-md text-bodyDark break-words'
                        : 'mt-2 rounded border-l-4 border-primary bg-grey-200 p-2'
                }>
                {!stackedLayout && (
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-grey-800">
                        Translated to {targetLanguageName}:
                    </div>
                )}
                {stackedLayout ? (
                    <p className="m-0 block w-full min-w-0 break-words text-left">
                        <span className="mr-1 text-sm font-medium text-grey-600">
                            {targetLanguageName}:
                        </span>
                        <span className="text-bodyDark">{translatedText}</span>
                    </p>
                ) : (
                    <div className="whitespace-pre-wrap break-words text-md text-bodyDark">
                        {translatedText}
                    </div>
                )}
            </div>
        ) : null

    if (stackedLayout) {
        return (
            <div className={clsx('flex w-full min-w-0 flex-col', className)}>
                <div className="flex min-w-0 items-center gap-2">
                    {children != null && <div className="min-w-0 flex-1">{children}</div>}
                    {buttonEl}
                </div>
                {error && <div className="mt-1 text-[10px] text-error">{error}</div>}
                {translationEl}
            </div>
        )
    }

    return (
        <div className={className}>
            {buttonEl}
            {error && <div className="mt-1 text-sm text-error">{error}</div>}
            {translationEl}
        </div>
    )
}

export default TranslateButton
